import { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api, formatApiErrorDetail, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";
import { CalendarBlank, ShieldCheck, ShieldWarning, Warning, FilePdf } from "@phosphor-icons/react";

/**
 * v3.2.20 · Admin-only license roster for Professional Engineers.
 *
 * Admin can view every PE's current license expiry date and set / update
 * it. Setting a future date on a `license_expired` PE reactivates them
 * back to `approved` (see backend `update_pe_license`).
 */
export default function AdminPELicensesPage() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [savingId, setSavingId] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/pe-license-roster");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load roster");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const stateBadge = (s) => {
    if (!s || s.status === "unknown")
      return { cls: "border-white/10 text-gray-400", label: "no date on file", icon: CalendarBlank };
    if (s.status === "expired")
      return { cls: "border-[#FF8A85]/50 text-[#FF8A85] bg-[#FF8A85]/5",
               label: `expired ${Math.abs(s.days_remaining)}d ago`, icon: Warning };
    if (s.status === "expiring_soon")
      return { cls: "border-[#FFCC00]/50 text-[#FFCC00] bg-[#FFCC00]/5",
               label: `${s.days_remaining}d left`, icon: ShieldWarning };
    return { cls: "border-emerald-500/40 text-emerald-400 bg-emerald-500/5",
             label: `${s.days_remaining}d left`, icon: ShieldCheck };
  };

  const save = async (r, newDate) => {
    setSavingId(r.id);
    try {
      const payload = { license_expiry_date: (newDate || "").trim() || null };
      const { data } = await api.patch(`/admin/pe-license/${r.id}`, payload);
      setRows((xs) => xs.map((x) => x.id === r.id ? {
        ...x,
        license_expiry_date: data.license_expiry_date,
        license_state:       data.license_state,
        pe_status:           data.pe_status,
      } : x));
      toast.success(`Updated · ${r.name || r.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingId(""); }
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-licenses" />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Admin</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter"
            data-testid="admin-licenses-heading">
          PE License Roster
        </h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          Set or update each PE&apos;s license expiry date. Reminders auto-fire at
          14, 7, and 1 day out. On expiry, the PE is soft-suspended (new
          proposals blocked) and every affected client is notified.
        </p>

        {busy ? (
          <div className="mt-6 text-gray-500 font-mono text-sm" data-testid="admin-licenses-loading">
            Loading roster…
          </div>
        ) : rows.length === 0 ? (
          <div className="mt-6 text-gray-500 font-mono text-sm">No PEs on file.</div>
        ) : (
          <div className="mt-6 border border-white/10" data-testid="admin-licenses-table">
            <div className="grid grid-cols-[1.4fr_140px_160px_150px_150px_120px] gap-2 px-4 py-2 border-b border-white/10 bg-black/40 font-mono text-[10px] uppercase tracking-wider text-gray-500">
              <div>PE</div>
              <div>Status</div>
              <div>Expiry date</div>
              <div>License state</div>
              <div>Uploaded file</div>
              <div className="text-right">Actions</div>
            </div>
            {rows.map((r) => {
              const b = stateBadge(r.license_state);
              const Icon = b.icon;
              return (
                <LicenseRow
                  key={r.id}
                  row={r}
                  badge={b}
                  Icon={Icon}
                  saving={savingId === r.id}
                  onSave={(d) => save(r, d)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function LicenseRow({ row, badge, Icon, saving, onSave }) {
  const [draft, setDraft] = useState(row.license_expiry_date || "");
  useEffect(() => { setDraft(row.license_expiry_date || ""); }, [row.license_expiry_date]);
  const dirty = (draft || "") !== (row.license_expiry_date || "");

  // v3.2.22 · Admin views the uploaded license by opening a new tab
  // with the streaming endpoint + bearer token as a query arg? No — the
  // backend expects Authorization header. So use a fetch → blob → object
  // URL pattern via a click handler.
  const [dl, setDl] = useState(false);
  const viewFile = async () => {
    setDl(true);
    try {
      const resp = await api.get(`/admin/pe-license/${row.id}/file`,
                                    { responseType: "blob" });
      const blob = resp.data;
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30 * 1000);
    } catch {
      // silent — admin will see the row didn't open
    } finally { setDl(false); }
  };

  return (
    <div className="grid grid-cols-[1.4fr_140px_160px_150px_150px_120px] gap-2 px-4 py-3 border-b border-white/5 items-center hover:bg-white/[0.02]"
         data-testid={`admin-license-row-${row.id}`}>
      <div>
        <div className="text-white text-sm">{row.name || "—"}</div>
        <div className="text-gray-500 text-xs font-mono truncate">{row.email}</div>
      </div>
      <div>
        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
          row.pe_status === "license_expired"
            ? "border-[#FF8A85]/50 text-[#FF8A85] bg-[#FF8A85]/5"
            : row.pe_status === "approved"
              ? "border-[#00E5FF]/40 text-[#00E5FF] bg-[#00E5FF]/5"
              : "border-white/10 text-gray-400"
        }`}
        data-testid={`admin-license-status-${row.id}`}>
          {row.pe_status || "draft"}
        </span>
      </div>
      <div>
        <input
          type="date"
          value={(draft || "").slice(0, 10)}
          onChange={(e) => setDraft(e.target.value)}
          className="w-full bg-black/40 border border-white/10 px-2 py-1.5 text-sm font-mono text-white focus:border-[#00E5FF] outline-none"
          data-testid={`admin-license-input-${row.id}`}
        />
      </div>
      <div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 border ${badge.cls}`}
              data-testid={`admin-license-state-${row.id}`}>
          <Icon size={12} weight="fill" /> {badge.label}
        </span>
      </div>
      {/* v3.2.22 · Uploaded license file — admin clicks to view. */}
      <div>
        {row.license_file_present ? (
          <button
            onClick={viewFile}
            disabled={dl}
            className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-40"
            title={row.license_file_name || "License file"}
            data-testid={`admin-license-view-file-${row.id}`}>
            <FilePdf size={12} weight="fill" /> {dl ? "opening…" : "view file"}
          </button>
        ) : (
          <span className="text-[10px] font-mono text-gray-500"
                data-testid={`admin-license-no-file-${row.id}`}>
            no upload
          </span>
        )}
      </div>
      <div className="text-right">
        <button
          onClick={() => onSave(draft)}
          disabled={saving || !dirty}
          className="text-xs font-mono px-3 py-1.5 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`admin-license-save-${row.id}`}>
          {saving ? "Saving…" : dirty ? "Save" : "—"}
        </button>
      </div>
    </div>
  );
}
